/**
 * NavigateDialog — in-app Google Maps turn-by-turn navigation.
 *
 * Drivers press "Navigate" on any route stop and get a full-screen dialog
 * with an embedded Google Maps Directions view — no need to leave the
 * ShipitNow app. Uses the `google.com/maps/embed/v1/directions` iframe
 * so the map, route line, and turn-by-turn steps render natively; we
 * add a small "Open in Google Maps" button as a fallback if the driver
 * wants full voice guidance.
 *
 * Requires `platform_settings.google_maps_api_key` (Maps Embed API
 * enabled). If missing, we fall back to a friendly note + the standard
 * `https://maps.google.com/?q=...` deep link.
 */
import React, { useEffect, useState } from 'react';
import { Navigation2, ExternalLink, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { publicSettings } from '../services/publicSettings';

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {{lat:number, lng:number} | null} props.origin      driver's current GPS
 * @param {{lat:number, lng:number, address?:string, label?:string}} props.destination  stop being navigated to
 * @param {string} [props.mode]  driving | bicycling | walking | two-wheeler
 */
const NavigateDialog = ({ open, onClose, origin, destination, mode = 'driving' }) => {
  const [apiKey, setApiKey] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    publicSettings.get().then((s) => {
      if (!alive) return;
      setApiKey(s?.google_maps_api_key || null);
      setLoading(false);
    }).catch(() => { if (alive) { setApiKey(null); setLoading(false); } });
    return () => { alive = false; };
  }, [open]);

  const originStr = origin && origin.lat != null && origin.lng != null
    ? `${origin.lat},${origin.lng}`
    : null;
  const destStr = destination && destination.lat != null && destination.lng != null
    ? `${destination.lat},${destination.lng}`
    : (destination?.address || null);
  // If we have both origin + destination, request full directions (route line
  // + turn-by-turn). Otherwise fall back to a single-point `place` embed so
  // the driver still sees the destination pin + surroundings while the
  // browser is still resolving their GPS permission.
  const embedUrl = apiKey && destStr
    ? (originStr
        ? `https://www.google.com/maps/embed/v1/directions?key=${apiKey}&origin=${encodeURIComponent(originStr)}&destination=${encodeURIComponent(destStr)}&mode=${mode}`
        : `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(destStr)}&zoom=15`)
    : null;
  const externalUrl = destStr
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destStr)}${originStr ? `&origin=${encodeURIComponent(originStr)}` : ''}&travelmode=${mode}`
    : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-4xl w-[95vw] h-[80vh] p-0 overflow-hidden flex flex-col"
        data-testid="navigate-dialog"
      >
        <DialogHeader className="px-5 py-3 border-b border-slate-200 flex-row items-center justify-between space-y-0">
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <Navigation2 className="h-5 w-5 text-emerald-600" />
            Navigate to {destination?.label || 'stop'}
          </DialogTitle>
          <div className="flex items-center gap-2">
            {externalUrl && (
              <a
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-xs font-medium"
                data-testid="open-in-google-maps"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Open in Google Maps
              </a>
            )}
            <button
              onClick={onClose}
              className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center"
              aria-label="Close navigation"
              data-testid="navigate-close"
            >
              <X className="h-4 w-4 text-slate-500" />
            </button>
          </div>
        </DialogHeader>
        {destination?.address && (
          <div className="px-5 py-2 text-xs text-slate-500 border-b border-slate-100 truncate">
            Destination: <span className="text-slate-800 font-medium">{destination.address}</span>
          </div>
        )}
        {!loading && apiKey && !originStr && (
          <div className="px-5 py-2 text-[11px] text-amber-800 bg-amber-50 border-b border-amber-200" data-testid="nav-gps-hint">
            Enable location on your device to see turn-by-turn directions from your current spot.
          </div>
        )}
        <div className="flex-1 relative bg-slate-50 navigate-iframe-wrap">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
            </div>
          )}
          {!loading && embedUrl && (
            <iframe
              title="Google Maps navigation"
              src={embedUrl}
              className="absolute inset-0 w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allow="geolocation"
              data-testid="navigate-iframe"
            />
          )}
          {!loading && !embedUrl && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center gap-3">
              <Navigation2 className="h-8 w-8 text-slate-400" />
              <div className="text-sm text-slate-700 max-w-md">
                In-app navigation needs a Google Maps API key with the <span className="font-semibold">Maps Embed API</span> enabled. Ask an admin to add it under <span className="font-semibold">Admin → Settings → Google Maps</span>.
              </div>
              {externalUrl && (
                <Button asChild className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <a href={externalUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-1.5" /> Open in Google Maps
                  </a>
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NavigateDialog;
